import { Trip, getCurrentTrip } from './trip';
import { logger } from './logger';
import { getGlobalObject } from './utils';
import { searchSeats, searchSeatsViewOnly } from './api';
import { getAssetManifest, loadAssets } from './assets';
import { GetViewOnlySeatsParams, Upsell } from './types';

export async function getSeats(_trip: Trip, _configuration: Upsell) {
  const global = getGlobalObject<Window>();
  const { id: tripId, tripAccessToken, searchIds } = _trip;

  const { assets, url } = await getAssetManifest('seats', _configuration.manifestUrl);

  await loadAssets(assets, url);

  if (global.gordianInternal) {
    logger.log('Initializing seatmap');

    await global.gordianInternal.init({ onTrack: () => ({}) });
    const triggerContainer = document.createElement('div');
    triggerContainer.id = 'gordian-seats-card';
    _configuration.container.append(triggerContainer);
    if (_configuration.display === 'card') {
      global?.gordianInternal?.seatMapTrigger(triggerContainer);
    }

    const searchParams = global.__GORDIAN__.searchParams ? global.__GORDIAN__.searchParams : {};

    try {
      const seatJson = await searchSeats(tripId, tripAccessToken, searchIds?.seat, searchParams);

      const modalcontainer = document.createElement('div');
      modalcontainer.id = 'gordian-seat-map-container';
      _configuration.container.append(modalcontainer);
      if (_configuration.display === 'card') {
        global?.gordianInternal?.renderSeatmap(modalcontainer, {
          showOnLoad: false,
          getSeatJson: Promise.resolve(seatJson),
          onBasketChange: onSeatSelection,
          onError: (error: any) => {
            logger.error(error);
          },
          theme: _configuration.theme,
        });
      }
      if (_configuration.display === 'modal') {
        global?.gordianInternal?.renderSeatmap(modalcontainer, {
          showOnLoad: true,
          getSeatJson: Promise.resolve(seatJson),
          onBasketChange: onSeatSelection,
          onError: (error: any) => {
            logger.error(error);
          },
          theme: _configuration.theme,
        });
      }
      if (_configuration.display === 'embedded') {
        global?.gordianInternal?.renderSeatmap(modalcontainer, {
          showOnLoad: false,
          getSeatJson: Promise.resolve(seatJson),
          modal: false,
          onBasketChange: onSeatSelection,
          onError: (error: any) => {
            logger.error(error);
          },
          theme: _configuration.theme,
        });
      }
    } catch (error) {
      global?.gordianInternal?.noSeatsOnSearch();

      const callbackFail = global?.__GORDIAN__?.eventCallbacks?.onSeatFail;
      if (callbackFail) {
        callbackFail();
      }

      logger.error(`Seat search failed ${error}`);
    }
  }
}

export async function getViewOnlySeats({
  journeys,
  language,
  currency,
  country,
  configuration,
  searchToken,
}: GetViewOnlySeatsParams) {
  const global = getGlobalObject<Window>();

  const { assets, url } = await getAssetManifest('seats', configuration.manifestUrl);
  await loadAssets(assets, url);

  if (global.gordianInternal) {
    logger.log('Initializing view-only seatmap');
    try {
      const seatJson = await searchSeatsViewOnly({
        journeys,
        language,
        currency,
        country,
        searchToken,
      });

      const containerId = 'gordian-view-only-seat-map-container';
      const seatmapContainer = document.createElement('div');
      seatmapContainer.id = containerId;
      configuration.container.append(seatmapContainer);

      const renderOptions: any = {
        getSeatJson: Promise.resolve(seatJson),
        onError: (error: any) => {
          logger.error(`View-only seatmap render error: ${error}`);
        },
        theme: configuration.theme,
        onBasketChange: undefined,
        modal: configuration.display !== 'embedded',
        showOnLoad: configuration.display !== 'card',
        agentBrand: configuration.brand,
      };

      global?.gordianInternal?.renderSeatmapViewOnly(seatmapContainer, renderOptions);
      logger.log(`View-only seatmap rendered into container #${containerId}`);
    } catch (error) {
      logger.error(`View-only seat search failed: ${error}`);

      const callbackFail = global?.__GORDIAN__?.eventCallbacks?.onViewOnlySeatFail;
      if (callbackFail) {
        callbackFail();
      }
    }
  } else {
    logger.error('gordianInternal not found. Cannot render view-only seatmap.');
  }
}

export async function onSeatSelection(
  _seatJSON: any,
  SeatmapBasket: any = { seats: [] }
): Promise<any> {
  logger.log('Calling onSeatSelection');
  const products = SeatmapBasket.seats.map((seat: any) => {
    const quantity: number = 1;
    const { product_id, passenger_id } = seat;
    return {
      product_id,
      passenger_id,
      quantity,
    };
  });
  const payload = products;
  const {
    id: tripId,
    tripAccessToken,
    onBasketChange = (...args: any[]) => {
      return args;
    },
  } = getCurrentTrip();
  const basketRequest = await fetch(
    `https://api.gordiansoftware.com/v2.2/trip/${tripId}/basket/seat`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tripAccessToken}`,
      },
      body: JSON.stringify(payload),
    }
  );
  const basket = await basketRequest.json();

  return Promise.resolve(onBasketChange(basket));
}
